<template>
  <div class="configurator-modal configurator-modal--add-to-cart px-0">
    <div class="configurator-modal__confetti"></div>
    <div class="configurator-modal__overlay" @click="closeModal"></div>
    <div class="container-xl">
      <div class="row mx-auto">
        <div class="mx-auto configurator-modal__card col-12 col-md-10">
          <!-- Title -->
          <h3
            class="mt-2 mt-sm-0 pe-8 pe-lg-4 mb-lg-2"
            @click="handleLaunchConfetti"
          >
            Gelukt! Het pakket vind je in je winkelwagen
          </h3>

          <!-- Close -->
          <div class="configurator-modal__close d-flex w-100">
            <button
              class="btn btn--gray btn--icon-lg btn--rotate btn--icon"
              @click="closeModal"
            >
              <i class="icon icon-close"></i>
            </button>
          </div>

          <!-- Product details -->
          <div
            class="row configurator-modal__content configurator-modal--horizontal py-3"
          >
            <!-- Image -->
            <div
              class="col-auto d-flex pe-0"
              v-if="getPackageDataImage(packageDataStore)"
            >
              <figure
                class="rimg rimg--contain rimg--100-100 configurator-modal__image w-100"
              >
                <img
                  class=""
                  :src="getPackageDataImage(packageDataStore).medium"
                  :alt="packageDataStore.packageName"
                  loading="lazy"
                />
              </figure>
            </div>

            <!-- Action & price -->
            <div
              class="col-auto col-lg d-flex flex-column justify-content-center align-items-start gap-05"
            >
              <!-- Brievenbus label -->
              <div v-if="showBrievenbusLabel()" class="badge badge--sm mb-05">
                Brievenbus
              </div>

              <!-- Title -->
              <span class="h5 text-wrap-balance mb-0">{{
                decodeHtml(packageDataStore.packageName)
              }}</span>

              <div class="d-flex align-items-center gap-2">
                <!-- Buttons -->
                <QuantityInput
                  :value="packageDataStoreLocalQuantity || 0"
                  :min="10"
                  :max="99999"
                  :step="1"
                  size="md"
                  :aria-label="'Aantal pakketten'"
                  @update:value="updateQuantity"
                />

                <!-- Price -->
                <div class="d-flex align-items-baseline gap-1">
                  <strong class="d-block mb-0">{{
                    formatPrice(packagePrice, getPriceFormat())
                  }}</strong
                  ><span class="d-block text-gray-500 fs-xs mt-n05"
                    >ex. BTW</span
                  >
                </div>
              </div>
            </div>

            <!-- Buttons -->
            <div
              class="col-12 col-lg-auto d-flex flex-column justify-content-center align-items-start gap-2 pt-4 pt-lg-0"
            >
              <a href="/winkelmandje" class="btn btn--cta w-100"
                >Naar winkelwagen <i class="icon icon-bag"></i
              ></a>
              <a
                @click="openPackageTypeModal"
                class="btn btn--gray w-100"
                :disabled="isSaving"
                ><span v-if="!isSaving">Nog een pakket samenstellen</span>
                <span v-else>Opslaan...</span></a
              >
            </div>
          </div>

          <!-- Cards -->
          <template v-if="!hasChosenCard">
            <div
              class="row configurator-modal__content configurator-modal--horizontal pt-6 pb-3"
            >
              <h4 class="mb-0">Vergeet je gepersonaliseerde kaart niet!</h4>
            </div>

            <div
              class="row g-2 configurator-modal__content configurator-modal--horizontal"
              role="grid"
              aria-labelledby="personalized-cards-section"
            >
              <!-- Cards loading -->
              <div
                v-if="cardsLoading"
                class="col-6 col-md-4"
                v-for="n in 3"
                :key="n"
              >
                <div class="skeleton skeleton--product-card-3lines"></div>
              </div>

              <!-- Cards loaded -->
              <div
                v-else
                class="col-6 col-md-4"
                v-for="card in cards"
                :key="card.id"
                role="gridcell"
              >
                <Card
                  :product="card"
                  :isPersonalizedCard="true"
                  :selected="isSelectedCard(card)"
                  @select="selectCard(card)"
                />
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, watch } from 'vue';
import {
  calculatePackagePrice,
  formatPrice,
  getPriceFormat,
} from '../utils/priceUtils.js';
import { decodeHtml, getPackageDataImage } from '../utils/utils.js';
import { isValidCard, loadCards } from '../utils/cardUtils.js';
import { launchConfetti } from '../utils/confettiUtils.js';
import QuantityInput from '../components/QuantityInput.vue';
import Card from '../templates/Card.vue';

// Props
const props = defineProps({
  packageData: { type: Object, required: true },
  packageType: { type: String, default: 'large' },
  isSaving: { type: Boolean, default: false },
});

// Data
const hasChosenCard = ref(false);
const cards = ref([]);
const cardsLoading = ref(true);
const packageDataStore = ref({});
const packageDataStoreLocalQuantity = ref(0);
const isSaving = ref(false);
const lastClickTime = ref(0);
const packagePrice = ref(0);

// Emits
const emit = defineEmits([
  'decrease',
  'increase',
  'close-modal',
  'open-package-type-modal',
]);

const updatePackagePrice = async () => {
  try {
    const price = await calculatePackagePrice(packageDataStore.value);
    packagePrice.value = price;
  } catch {
    packagePrice.value = 0;
  }
};

onMounted(async () => {
  packageDataStore.value = { ...props.packageData };
  packageDataStoreLocalQuantity.value = props.packageData?.quantity || 0;

  hasChosenCard.value =
    packageDataStore.value?.personalization?.selectedCard !== null;

  await updatePackagePrice();

  loadCards(3, true)
    .then((cardsData) => {
      cardsData.length = Math.min(cardsData.length, 3);
      cards.value = cardsData;
      cardsLoading.value = false;
    })
    .catch((error) => {
      console.error('Error loading cards', error);
      cardsLoading.value = false;
    });

  setTimeout(() => {
    handleLaunchConfetti();
  }, 100);
});

watch(
  [packageDataStore, packageDataStoreLocalQuantity],
  () => {
    updatePackagePrice();
  },
  { deep: true },
);

const showBrievenbusLabel = () => {
  // Alleen tonen bij groot pakket (niet bij brievenbuspakket)
  if (props.packageType === 'small') return false;

  // Check of product geschikt is voor brievenbus
  const attrs = packageDataStore.value.attributes || [];

  const brievenbusAttr = attrs.find((attr) => {
    // Probeer verschillende manieren om het brievenbus attribuut te vinden
    const nameMatch =
      attr.name === 'pa_brievenbus-geschikt' ||
      attr.slug === 'pa_brievenbus-geschikt' ||
      attr.name?.toLowerCase().includes('brievenbus') ||
      attr.slug?.toLowerCase().includes('brievenbus') ||
      attr.name?.toLowerCase().includes('geschikt') ||
      attr.slug?.toLowerCase().includes('geschikt');
    return nameMatch;
  });

  // Product heeft geen brievenbus attribuut
  if (!brievenbusAttr) return false;

  const valueMatch =
    brievenbusAttr.options?.includes?.('ja') || brievenbusAttr.value === 'ja';

  return valueMatch;
};

// Close modal
const closeModal = () => {
  emit('close-modal');
};

// Open package type modal
const openPackageTypeModal = async () => {
  const now = Date.now();

  // Prevent rapid clicks (debounce)
  if (isSaving.value || now - lastClickTime.value < 1000) return;

  lastClickTime.value = now;

  // Disable button during save
  isSaving.value = true;

  try {
    emit('open-package-type-modal');
  } catch (error) {
    console.error('Error in openPackageTypeModal:', error);
  } finally {
    // Re-enable button after a short delay
    setTimeout(() => {
      isSaving.value = false;
    }, 500);
  }
};

// Launch confetti
const handleLaunchConfetti = () => {
  launchConfetti('.configurator-modal__confetti', [180, 360], [120, 180], {
    y: 0,
    x: 0.5,
  });
};

const debounce = (callback, wait) => {
  let timeoutId = null;
  return (...args) => {
    window.clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => {
      callback(...args);
    }, wait);
  };
};

// Update data which is already in the cart
const handleUpdateCartItem = (changes = {}) => {
  // Copy local data to prevent race conditions
  const packageDataLocal = { ...packageDataStore.value };

  // Get stored packages
  const stored = localStorage.getItem('composedPackages');
  let existingPackages = stored ? JSON.parse(stored) : [];

  // Try to find package by timestamp or by comparing key fields
  let foundIndex = -1;

  // First try: find by timestamp if available
  if (packageDataLocal.timestamp) {
    foundIndex = existingPackages.findIndex(
      (pkg) => pkg.timestamp === packageDataLocal.timestamp,
    );
  }

  // Second try: find by comparing key identifying fields
  if (foundIndex === -1) {
    foundIndex = existingPackages.findIndex((existingPackage) => {
      const sameBasePackage =
        (existingPackage.basePackage?.id || existingPackage.basePackage?.ID) ===
        (packageDataLocal.basePackage?.id || packageDataLocal.basePackage?.ID);

      const samePackageName =
        existingPackage.packageName === packageDataLocal.packageName;

      const sameProducts =
        JSON.stringify(existingPackage.products || []) ===
        JSON.stringify(packageDataLocal.products || []);

      const sameFoodDrinks =
        JSON.stringify(existingPackage.foodDrinks || []) ===
        JSON.stringify(packageDataLocal.foodDrinks || []);

      return (
        sameBasePackage && samePackageName && sameProducts && sameFoodDrinks
      );
    });
  }

  // If still not found, use the last item (fallback)
  if (foundIndex === -1 && existingPackages.length > 0) {
    foundIndex = existingPackages.length - 1;
  }

  if (foundIndex === -1 || foundIndex >= existingPackages.length) {
    return;
  }

  // Update the data!
  // Quantity
  if (changes.quantity) packageDataLocal.quantity = changes.quantity;
  if (changes.card)
    packageDataLocal.personalization.selectedCard = changes.card;

  // Preserve timestamp
  if (!packageDataLocal.timestamp && existingPackages[foundIndex].timestamp) {
    packageDataLocal.timestamp = existingPackages[foundIndex].timestamp;
  }

  // Add updated package to stored packages & store new data
  existingPackages[foundIndex] = { ...packageDataLocal };
  localStorage.setItem('composedPackages', JSON.stringify(existingPackages));

  // Update local data
  packageDataStore.value = packageDataLocal;
};
const handleUpdateCartItemDebounce = debounce(handleUpdateCartItem, 1000);

// Update quantity of cart
const updateQuantity = (newQuantity) => {
  handleUpdateCartItemDebounce({
    quantity: newQuantity,
  });
  packageDataStoreLocalQuantity.value = newQuantity;
  packageDataStore.value = {
    ...packageDataStore.value,
    quantity: newQuantity,
  };
};

// Check if card is selected
const isSelectedCard = (card) => {
  return packageDataStore.value?.personalization?.selectedCard?.id === card.id;
};

// Change card data and update cart
const selectCard = (card) => {
  if (!isValidCard(card)) {
    return;
  }

  const selectedCard = isSelectedCard(card) ? null : card;
  handleUpdateCartItem({
    card: selectedCard,
  });

  if (packageDataStore.value.personalization) {
    packageDataStore.value.personalization.selectedCard = selectedCard;
  }
};
</script>
